import { faTrash } from "@fortawesome/pro-solid-svg-icons"
import { Alert, Button, Popconfirm, Space, Spin, Table, Typography } from "antd"
import { ColumnsType } from "antd/lib/table"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { DateTime } from 'luxon'
import { ApiKey, getApiKeys, deleteApiKey, createApiKey, getFeatureGrants, ApiKeyQueryKey, FeatureGrantsQueryKey, ZapierGrant } from "./apiKeysModel"
import Upsell from "./Upsell"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

const { Text, Link } = Typography

const ApiKeys = () => {
  const queryClient = useQueryClient();
  const query = useQuery(ApiKeyQueryKey, getApiKeys)
  const grants = useQuery(FeatureGrantsQueryKey, getFeatureGrants)

  const createApiKeyMutation = useMutation(() => createApiKey(queryClient))

  const deleteApiKeyMutation = useMutation((id: number) => deleteApiKey(id, queryClient))

  const createNewApiKey = () => createApiKeyMutation.mutate()

  const columns: ColumnsType<ApiKey> = [
    {
      title: 'Key',
      dataIndex: 'maskedKey',
      render: x => <Text code>{x}...</Text>
    },
    {
      title: 'Created',
      render: (_, row) => {
        return <>
          {DateTime.fromISO(row.createdAt).toLocaleString(DateTime.DATETIME_SHORT)} by <em>{row.createdByEmail}</em>
        </>
      }
    },
    {
      title: 'Last Used',
      render: (_, row) => {
        return <>
          {row.lastUsedAt ? DateTime.fromISO(row.lastUsedAt).toLocaleString(DateTime.DATETIME_SHORT) : 'Never'}
        </>
      }
    },
    {
      render: (_, row) => <Popconfirm
        title="Are you sure you want to delete this API key?"
        okText="Yes"
        cancelText="No"
        onConfirm={() => deleteApiKeyMutation.mutate(row.id)}
      >
        <Button data-testid={`delete-key-${row.id}`} type="text" danger icon={<FontAwesomeIcon icon={faTrash} />} />
      </Popconfirm>
    }
  ]

  if (grants.isLoading) {
    return <Spin />
  } else if (!grants.data?.includes(ZapierGrant)) {
    return <Upsell />
  }

  return <Space direction="vertical" style={{ width: '100%' }}>
    <h3>Setup Instructions</h3>
    <p>
      In order to use the SynkedUP actions and triggers in Zapier
      follow these steps:
    </p>
    <ol>
      <li>
        <Link onClick={createNewApiKey}>Create an API key</Link> (or use one you previously created).

        {createApiKeyMutation.isLoading && <>
          <Spin tip="Generating New API Key">
            <Alert type="success" showIcon message="New API Key" description="Generating a new API key" />
          </Spin>

        </>}
        {createApiKeyMutation.isSuccess && <>
          <Alert type="success" showIcon message="New API Key" description={<Space direction="vertical">
            <Text copyable code style={{ fontSize: '16px', overflowWrap: 'anywhere' }}>{createApiKeyMutation.data}</Text>
            <p>Save this API key in a secure place. You will not be able to see it again after leaving this page.</p>
          </Space>} />
        </>}
      </li>
      <li>
        Use <Link href="https://zapier.com/developer/public-invite/167944/52d053c410b14bb8652347082c8c7795/" target="_blank">this link</Link> to
        give your Zapier account access to the SynkedUP integration.
      </li>
      <li>
        When building your Zap, provide the API key when asked to "Allow Zapier to access your SynkedUP account."
      </li>
    </ol>

    <h3>Previously Created Keys</h3>
    <p>
      API keys you've created have access to edit data in your SynkedUP account.
      If you are no longer using an API key or it was shared with someone who no longer needs access,
      you can delete the key from the table below.
      Once deleted, all Zaps that use the API key will no longer function.
    </p>

    <Table dataSource={query.data} columns={columns} loading={query.isLoading} rowKey={(row) => row.maskedKey} />
  </Space>
}
export default ApiKeys
